import { useEffect } from 'react';
import { type RouteContext } from 'react-resource-router';

/* eslint-disable no-restricted-imports */
import traceUFOPageLoad from '@atlaskit/react-ufo/trace-pageload';
import traceUFOTransition from '@atlaskit/react-ufo/trace-transition';
import { useOneTrustContext } from '@app/onetrust';

/**
 * Central consent-aware UFO watcher hook.
 *
 * This hook should be called ONCE from a central location (App.tsx) rather
 * than from individual page components. It tracks:
 *   - traceUFOPageLoad: on initial app load
 *   - traceUFOTransition: on subsequent route changes (navigating between pages)
 *
 * The experience name is derived from the matched route's name, keeping UFO
 * experience keys consistent with the route definitions in Routes.ts.
 *
 * Gated behind OneTrust Performance Cookies consent (category 2).
 *
 * NOTE: traceUFOPageLoad and traceUFOTransition should not be called directly
 * from page components — only from this central watcher. Individual pages
 * should use <UFOSegment> and <UFOLoadHold> for their interaction contexts.
 *
 * IMPORTANT: We use routeContext.action to distinguish between an initial page
 * load (action = 'POP' or '') and a user-initiated navigation (action = 'PUSH'
 * or 'REPLACE'). On Community (community.atlassian.com), react-resource-router
 * may resolve the route object twice during initial mount (e.g. first matching
 * 'app-updates', then correcting to 'app-updates-details' for a details URL).
 * Using a boolean isInitialLoad flag caused traceUFOTransition to fire on the
 * second resolution, aborting the page-load interaction with reason 'transition'.
 * By keying on action='PUSH'|'REPLACE' for transitions, we correctly treat all
 * initial resolutions as page-load events and only trace transitions on explicit
 * user navigations.
 *
 * @param routeContext - The router state from useRouter() (inside a <Router>)
 *
 * @example
 * // In AppContents (inside <Router>):
 * const [routerState] = useRouter();
 * useReactUFOWatcher(routerState);
 */
export const useReactUFOWatcher = (routeContext: RouteContext): void => {
  const { analyticsOptOut } = useOneTrustContext();

  // useEffect ensures trace calls only fire when the route actually changes,
  // not on every re-render. We key on action to distinguish initial page loads
  // (action='POP'|'') from user-initiated navigations (action='PUSH'|'REPLACE').
  useEffect(() => {
    if (analyticsOptOut || !routeContext.route) {
      return;
    }

    const experienceName = routeContext.route.name.toLowerCase();
    const isUserNavigation =
      routeContext.action === 'PUSH' || routeContext.action === 'REPLACE';

    if (isUserNavigation) {
      traceUFOTransition(experienceName);
    } else {
      // action='POP' (browser back/forward or initial load) or '' (initial mount)
      traceUFOPageLoad(experienceName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeContext.route, routeContext.action, analyticsOptOut]);
};
